import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield, RefreshCw, AlertCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const scoreClass = (n) =>
  n >= 90 ? "bg-[#0F4D2C]" : n >= 70 ? "bg-[#C9A227]" : n >= 40 ? "bg-[#D97706]" : "bg-[#B91C1C]";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const AdminReadiness = () => {
  const [token, setToken] = useState(() => localStorage.getItem("yh_admin_token") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);

  const fetchRows = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/readiness/submissions`, {
        headers: { "x-admin-token": t },
      });
      if (res.status === 401) {
        setError("Unauthorized — check your admin token.");
        setAuthed(false);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setRows(data);
      setAuthed(true);
      localStorage.setItem("yh_admin_token", t);
    } catch (e) {
      setError(e.message || "Failed to load submissions");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchRows(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-6 lg:p-10" data-testid="admin-readiness">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
              YOUR HOPE · ADMIN
            </p>
            <h1 className="font-display text-3xl md:text-4xl text-[#0B2447] font-semibold mt-1">
              Readiness Check submissions
            </h1>
            <p className="text-sm text-[#0B2447]/70 mt-1">
              Newest submissions first. Contact leads directly via the email listed.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="admin-token" className="text-xs text-[#0B2447]/70">
                Admin token
              </Label>
              <Input
                id="admin-token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-1 bg-white border-[#0B2447]/20 focus-visible:ring-[#C9A227] w-64"
                placeholder="Enter admin token"
                data-testid="admin-token-input"
              />
            </div>
            <Button
              onClick={() => fetchRows(token)}
              disabled={!token || loading}
              className="bg-[#0B2447] hover:bg-[#11254A] text-white"
              data-testid="admin-load"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin mr-2" />
              ) : (
                <RefreshCw size={14} className="mr-2" />
              )}
              Load
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-[#B91C1C] bg-[#B91C1C]/5 border border-[#B91C1C]/20 px-4 py-3 rounded-sm mb-4">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {!authed && !error && (
          <Card className="p-8 text-center bg-white border-[#0B2447]/10">
            <Shield size={36} className="text-[#C9A227] mx-auto" />
            <p className="font-display text-xl text-[#0B2447] font-semibold mt-3">
              Enter your admin token
            </p>
            <p className="text-sm text-[#0B2447]/70 mt-1">
              Set in <code>backend/.env</code> as <code>ADMIN_TOKEN</code>.
            </p>
          </Card>
        )}

        {authed && (
          <Card className="bg-white border-[#0B2447]/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0B2447] text-white text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide">Date</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide">Name</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide">Email</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide text-center">Files</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide">Stage</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide text-center">Coverage</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide text-center">Score</th>
                    <th className="px-4 py-3 font-semibold text-[12px] tracking-wide">Recommended</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0B2447]/10">
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-[#0B2447]/60 italic-serif">
                        No submissions yet.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-[#FDFBF7]" data-testid={`row-${r.id}`}>
                      <td className="px-4 py-3 text-[#0B2447]/80 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-[#0B2447] font-semibold">{r.name}</td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${r.email}`} className="text-[#C9A227] font-semibold hover:underline">
                          {r.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-center text-[#0B2447]/80">{r.file_count}</td>
                      <td className="px-4 py-3 text-[#0B2447]/80 whitespace-nowrap">{r.stage_label}</td>
                      <td className="px-4 py-3 text-center text-[#0B2447]/80">{r.evidence_coverage}%</td>
                      <td className="px-4 py-3 text-center">
                        {r.readiness_score != null ? (
                          <span
                            className={`inline-flex items-center justify-center min-w-[42px] px-2 py-1 rounded-full text-white text-[12px] font-bold ${scoreClass(r.readiness_score)}`}
                          >
                            {r.readiness_score}
                          </span>
                        ) : (
                          <span className="text-[#0B2447]/40 text-[12px] italic-serif">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#0B2447]/80">{r.recommended_next_service}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminReadiness;
